import React, {useEffect} from 'react';

import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ConfiguratorResultType} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {
  FlowchartEditorLoadError,
  FlowchartTaskOrSchemaInaccessibleError,
} from 'processes/modals/ProcessFlowchartModalShared';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {useAllSchemasByIdWithSingletonSchemas} from '../useAllSchemasById';
import {createInitialExecutionDraftState} from './createInitialExecutionDraftState';
import {useExecutionEditorConfigurator} from './executionFlowchartConfigurator';
import {FlowchartDataResultType, useFlowchartConfigData, useProcessData} from './executionFlowchartData';
import ExecutionFlowchartEditor from './ExecutionFlowchartEditor';
import {ExecutionViewerFlowchartPaneProps, PCMProcessData} from './ExecutionFlowchartPaneTypes';

function getFlowchartConfigIdFromProcess(processData: PCMProcessData | null | undefined): string | null {
  if (processData == null || isInaccessible(processData.taskSchemaOrInaccessible)) {
    return null;
  }
  return processData.taskSchemaOrInaccessible.flowchartConfig?.id ?? null;
}

function getExecutionFlowchartIdFromProcess(processData: PCMProcessData | null | undefined): string | null {
  if (processData == null || processData.tasks.length === 0) {
    return null;
  }
  return processData.tasks[0].executionFlowchart?.id ?? null;
}

export default function ExecutionViewerFlowchartPane(props: ExecutionViewerFlowchartPaneProps): JSX.Element {
  const {draftState, setDraftState} = props;
  const processId = props.processId ?? null;
  const processData = useProcessData(processId);

  const flowchartConfigId = props.flowchartConfigId ?? getFlowchartConfigIdFromProcess(processData);
  const executionFlowchartId =
    props.flowchartConfigId != null ? null : getExecutionFlowchartIdFromProcess(processData);

  const {configuratorResult, executionEditorConfigurator} = useExecutionEditorConfigurator(props.editorMode);
  const dataResult = useFlowchartConfigData(
    flowchartConfigId,
    executionEditorConfigurator,
    executionFlowchartId
  );
  const data = dataResult.resultType === FlowchartDataResultType.SUCCESS ? dataResult.data : null;

  const {
    schemasById: allSchemasById,
    processTaskDefinitionsResult: taskDefinitionsResult,
    singletonTaskSchemasByName,
  } = useAllSchemasByIdWithSingletonSchemas({
    flowchartEditorConfigurator: executionEditorConfigurator,
    rootTaskSchema: data?.rootTaskSchema ?? null,
    skipTaskDefinitionsQuery: true,
    availableTaskSchemas: data?.availableTaskSchemas,
  });

  useEffect(() => {
    if (data == null || executionEditorConfigurator == null || draftState != null) {
      return;
    }
    // TODO: PR 229196 have the parent initialize
    setDraftState(createInitialExecutionDraftState(data, props.editorMode, executionEditorConfigurator));
  }, [data, draftState, executionEditorConfigurator, props.editorMode, setDraftState]);

  if (processId != null && processData !== undefined && (processData == null || flowchartConfigId == null)) {
    return <FlowchartTaskOrSchemaInaccessibleError />;
  }

  if (
    configuratorResult.resultType === ConfiguratorResultType.ERROR ||
    dataResult.resultType === FlowchartDataResultType.ERROR
  ) {
    return <FlowchartEditorLoadError />;
  }

  if (dataResult.resultType === FlowchartDataResultType.INACCESSIBLE) {
    return <FlowchartTaskOrSchemaInaccessibleError />;
  }

  const isWaitingForProcessData = processId != null && processData === undefined;
  if (
    executionEditorConfigurator == null ||
    data == null ||
    allSchemasById == null ||
    isWaitingForProcessData ||
    draftState == null
  ) {
    return <CenteredHorizontalThrobber />;
  }

  return (
    <ExecutionFlowchartEditor
      editorMode={props.editorMode}
      data={data}
      executionEditorConfigurator={executionEditorConfigurator}
      draftState={draftState}
      setDraftState={setDraftState}
      onBlockingErrorsChange={props.onBlockingErrorsChange}
      allSchemasById={allSchemasById}
      taskDefinitionsResult={taskDefinitionsResult}
      singletonTaskSchemasByName={singletonTaskSchemasByName}
    />
  );
}
